import { useState } from "react";
import {
  Card, Button, Modal, Form, Input, InputNumber, Switch, message,
  Tag, Popconfirm, Divider, Spin, Tooltip,
} from "antd";
import { Plus, Pencil, Trash2, Languages, ChevronDown, ChevronUp, Building2, RefreshCw } from "lucide-react";
import { useIndustryList, useIndustrySave, useIndustryDelete } from "../../../api-hooks/industries";
import { TIndustry, TTerminology } from "../../../types";

const TERM_KEYS = [
  { key: "job",         label: "Job",         placeholder: { s: "Job",         p: "Jobs"         } },
  { key: "worker",      label: "Worker",       placeholder: { s: "Worker",      p: "Workers"      } },
  { key: "client",      label: "Client",       placeholder: { s: "Client",      p: "Clients"      } },
  { key: "location",    label: "Location",     placeholder: { s: "Site",        p: "Sites"        } },
  { key: "quote",       label: "Quote",        placeholder: { s: "Quote",       p: "Quotes"       } },
  { key: "invoice",     label: "Invoice",      placeholder: { s: "Invoice",     p: "Invoices"     } },
  { key: "task",        label: "Task",         placeholder: { s: "Task",        p: "Tasks"        } },
  { key: "team",        label: "Team",         placeholder: { s: "Team",        p: "Teams"        } },
  { key: "supervisor",  label: "Supervisor",   placeholder: { s: "Supervisor",  p: "Supervisors"  } },
  { key: "asset",       label: "Asset",        placeholder: { s: "Asset",       p: "Assets"       } },
  { key: "contract",    label: "Contract",     placeholder: { s: "Contract",    p: "Contracts"    } },
  { key: "report",      label: "Report",       placeholder: { s: "Report",      p: "Reports"      } },
  { key: "inspection",  label: "Inspection",   placeholder: { s: "Inspection",  p: "Inspections"  } },
  { key: "certificate", label: "Certificate",  placeholder: { s: "Certificate", p: "Certificates" } },
  { key: "schedule",    label: "Schedule",     placeholder: { s: "Schedule",    p: "Schedules"    } },
  { key: "material",    label: "Material",     placeholder: { s: "Material",    p: "Materials"    } },
  { key: "incident",    label: "Incident",     placeholder: { s: "Incident",    p: "Incidents"    } },
  { key: "visit",       label: "Visit",        placeholder: { s: "Visit",       p: "Visits"       } },
  { key: "project",     label: "Project",      placeholder: { s: "Project",     p: "Projects"     } },
  { key: "supplier",    label: "Supplier",     placeholder: { s: "Supplier",    p: "Suppliers"    } },
  { key: "ticket",      label: "Ticket",       placeholder: { s: "Ticket",      p: "Tickets"      } },
  { key: "note",        label: "Note",         placeholder: { s: "Note",        p: "Notes"        } },
];

export const DEFAULT_TERMINOLOGY: TTerminology[] = TERM_KEYS.map(({ key, placeholder }) => ({
  term_key: key,
  term_singular: placeholder.s,
  term_plural: placeholder.p,
}));

const POPULAR_ICONS = [
  "🐀", "🔥", "❄️", "⚡", "🔧", "🚿", "🛡️", "🌿",
  "🏗️", "🏢", "🌍", "🧹", "🔑", "🌲", "🚗", "🏥",
  "⚙️", "🔌", "🧱", "🌊", "🧯", "🏭", "🛠️", "🌾",
  "🎨", "🔲", "💻", "🍽️", "♻️", "🚛", "🎪", "☀️", "📷",
];

const LUCIDE_FALLBACK: Record<string, string> = {
  bug: "🐛", flame: "🔥", wind: "❄️", zap: "⚡", droplets: "🚿",
  sparkles: "🧹", shield: "🛡️", "tree-pine": "🌿", wrench: "🔧",
  "hard-hat": "🏗️", map: "🌍",
};

function resolveIcon(icon: string | undefined): string {
  if (!icon) return "🏭";
  if (LUCIDE_FALLBACK[icon]) return LUCIDE_FALLBACK[icon];
  return icon;
}

interface TerminologyEditorProps {
  value?: TTerminology[];
  onChange?: (v: TTerminology[]) => void;
  onFillDefaults?: () => void;
}

function TerminologyEditor({ value = [], onChange, onFillDefaults }: TerminologyEditorProps) {
  const getVal = (key: string, field: "term_singular" | "term_plural") =>
    value.find((t) => t.term_key === key)?.[field] ?? "";

  const setVal = (key: string, field: "term_singular" | "term_plural", val: string) => {
    const next = [...value];
    const idx = next.findIndex((t) => t.term_key === key);
    if (idx >= 0) {
      next[idx] = { ...next[idx], [field]: val };
    } else {
      next.push({ term_key: key, term_singular: field === "term_singular" ? val : "", term_plural: field === "term_plural" ? val : "" });
    }
    onChange?.(next);
  };

  return (
    <div className="space-y-0">
      <div className="flex items-center justify-between mb-2">
        <div className="grid grid-cols-[130px_1fr_12px_1fr] gap-x-2 items-center flex-1">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Term</span>
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Singular</span>
          <span />
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Plural</span>
        </div>
        <Button
          size="small"
          icon={<RefreshCw size={11} />}
          onClick={onFillDefaults}
          className="ml-2 shrink-0 text-violet-600 border-violet-300 hover:bg-violet-50"
        >
          Fill Defaults
        </Button>
      </div>
      <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1">
        {TERM_KEYS.map(({ key, label, placeholder }) => (
          <div key={key} className="grid grid-cols-[130px_1fr_12px_1fr] gap-x-2 items-center">
            <span className="text-xs text-slate-600 font-medium truncate">{label}</span>
            <Input
              size="small"
              placeholder={placeholder.s}
              value={getVal(key, "term_singular")}
              onChange={(e) => setVal(key, "term_singular", e.target.value)}
            />
            <span className="text-slate-300 text-xs text-center">/</span>
            <Input
              size="small"
              placeholder={placeholder.p}
              value={getVal(key, "term_plural")}
              onChange={(e) => setVal(key, "term_plural", e.target.value)}
            />
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 mt-2 pt-1 border-t border-slate-100">
        All 22 terms are saved for every industry. Blank fields save as system defaults.
      </p>
    </div>
  );
}

function IndustryCard({ industry, onEdit, onDelete }: { industry: TIndustry; onEdit: () => void; onDelete: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const termCount = industry.terminology?.length ?? 0;

  return (
    <Card
      className="rounded-2xl shadow-sm transition-all hover:shadow-md"
      style={{ borderColor: industry.is_active ? "#e9d5ff" : "#e2e8f0", borderWidth: 2 }}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-50 to-purple-100 flex items-center justify-center text-3xl border border-violet-100 shrink-0 shadow-sm">
          {resolveIcon(industry.icon)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="font-bold text-slate-800 text-base leading-tight">{industry.name}</div>
            <Tag color={industry.is_active ? "purple" : "default"} className="text-[10px] shrink-0">
              {industry.is_active ? "Active" : "Inactive"}
            </Tag>
          </div>
          <div className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed">
            {industry.description || <span className="text-slate-300 italic">No description</span>}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 text-xs text-slate-400">
        <span className="bg-slate-100 px-2 py-0.5 rounded-full">Sort #{industry.sort_order}</span>
        {termCount > 0 && (
          <span className="bg-violet-50 text-violet-600 px-2 py-0.5 rounded-full flex items-center gap-1">
            <Languages size={10} /> {termCount} terms
          </span>
        )}
      </div>

      {industry.terminology && industry.terminology.length > 0 && (
        <div className="mb-3">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-violet-600 hover:text-violet-800 transition-colors font-medium"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? "Hide" : "View"} terminology
          </button>
          {expanded && (
            <div className="mt-2 bg-slate-50 rounded-xl p-3 border border-slate-100 max-h-52 overflow-y-auto">
              {TERM_KEYS.map(({ key, label }) => {
                const term = industry.terminology?.find((t) => t.term_key === key);
                if (!term?.term_singular) return null;
                return (
                  <div key={key} className="flex items-center gap-3 text-xs py-0.5">
                    <span className="w-20 text-slate-400 shrink-0">{label}</span>
                    <span className="text-slate-700 font-semibold">
                      {term.term_singular}
                      {term.term_plural && <span className="font-normal text-slate-400"> / {term.term_plural}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <Button size="small" icon={<Pencil size={11} />} onClick={onEdit}>Edit</Button>
        <Tooltip title="This will deactivate the industry">
          <Popconfirm
            title="Deactivate this industry?"
            description="Companies using this industry keep their settings."
            onConfirm={onDelete}
            okText="Deactivate"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<Trash2 size={11} />}>Remove</Button>
          </Popconfirm>
        </Tooltip>
      </div>
    </Card>
  );
}

export default function Industries() {
  const [showModal, setShowModal] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState<TIndustry | null>(null);
  const [terminology, setTerminology] = useState<TTerminology[]>(DEFAULT_TERMINOLOGY);
  const [selectedIcon, setSelectedIcon] = useState<string>("🏭");
  const [messageApi, ctx] = message.useMessage();
  const [form] = Form.useForm();

  const { data: industries = [], isLoading } = useIndustryList();

  const { mutate: saveIndustry, isPending: saving } = useIndustrySave({
    onSuccess: () => {
      messageApi.success(editingIndustry ? "Industry updated." : "Industry created.");
      setShowModal(false);
    },
    onError: (err: any) => messageApi.error(err?.message || "Failed to save industry."),
  });

  const { mutate: deleteIndustry } = useIndustryDelete({
    onSuccess: () => messageApi.success("Industry deactivated."),
    onError: () => messageApi.error("Failed to remove industry."),
  });

  const active = industries.filter((i) => i.is_active);
  const inactive = industries.filter((i) => !i.is_active);

  function fillDefaults() {
    setTerminology(
      TERM_KEYS.map(({ key, placeholder }) => {
        const existing = terminology.find((t) => t.term_key === key);
        return {
          term_key: key,
          term_singular: existing?.term_singular || placeholder.s,
          term_plural: existing?.term_plural || placeholder.p,
        };
      })
    );
  }

  const openCreate = () => {
    setEditingIndustry(null);
    form.resetFields();
    const defaultIcon = "🏭";
    setSelectedIcon(defaultIcon);
    form.setFieldsValue({ is_active: true, sort_order: industries.length + 1, icon: defaultIcon });
    setTerminology(DEFAULT_TERMINOLOGY);
    setShowModal(true);
  };

  const openEdit = (industry: TIndustry) => {
    setEditingIndustry(industry);
    const icon = resolveIcon(industry.icon);
    setSelectedIcon(icon);
    form.setFieldsValue({
      name: industry.name, icon,
      description: industry.description,
      sort_order: industry.sort_order,
      is_active: industry.is_active,
    });
    const existing = industry.terminology ?? [];
    const merged = TERM_KEYS.map(({ key, placeholder }) => {
      const t = existing.find((x) => x.term_key === key);
      return {
        term_key: key,
        term_singular: t?.term_singular || placeholder.s,
        term_plural: t?.term_plural || placeholder.p,
      };
    });
    setTerminology(merged);
    setShowModal(true);
  };

  const handleSubmit = (vals: any) => {
    const allTerms = TERM_KEYS.map(({ key, placeholder }) => {
      const t = terminology.find((x) => x.term_key === key);
      return {
        term_key: key,
        term_singular: t?.term_singular?.trim() || placeholder.s,
        term_plural: t?.term_plural?.trim() || placeholder.p,
      };
    });
    saveIndustry({
      name: vals.name,
      icon: vals.icon || selectedIcon,
      description: vals.description,
      sort_order: vals.sort_order ?? 0,
      is_active: vals.is_active !== false,
      terminology: allTerms,
      id: editingIndustry?.id,
    });
  };

  const pickIcon = (emoji: string) => {
    setSelectedIcon(emoji);
    form.setFieldValue("icon", emoji);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Spin size="large" />
        <p className="text-slate-400 text-sm">Loading industries...</p>
      </div>
    );
  }

  return (
    <div>
      {ctx}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Industries</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            {active.length} active · {inactive.length} inactive · 22 terminology fields per industry
          </p>
        </div>
        <Button
          type="primary"
          icon={<Plus size={14} />}
          onClick={openCreate}
          style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
        >
          New Industry
        </Button>
      </div>

      {active.length > 0 && (
        <div className="mb-2">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Active ({active.length})</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
            {active.map((ind) => (
              <IndustryCard key={ind.id} industry={ind} onEdit={() => openEdit(ind)} onDelete={() => deleteIndustry(ind.id)} />
            ))}
          </div>
        </div>
      )}

      {inactive.length > 0 && (
        <div className="mb-2">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Inactive ({inactive.length})</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {inactive.map((ind) => (
              <IndustryCard key={ind.id} industry={ind} onEdit={() => openEdit(ind)} onDelete={() => deleteIndustry(ind.id)} />
            ))}
          </div>
        </div>
      )}

      {industries.length === 0 && (
        <div className="text-center py-20">
          <div className="text-5xl mb-4">🏭</div>
          <p className="text-slate-500 font-medium">No industries yet</p>
          <p className="text-slate-400 text-sm mt-1">Create your first industry to get started</p>
          <Button type="primary" icon={<Plus size={14} />} onClick={openCreate} className="mt-4" style={{ background: "#4c1d95", borderColor: "#4c1d95" }}>
            Create Industry
          </Button>
        </div>
      )}

      <Modal
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        title={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center text-2xl shadow-sm">
              {selectedIcon}
            </div>
            <div>
              <div className="font-semibold text-slate-800 text-base">
                {editingIndustry ? `Edit: ${editingIndustry.name}` : "Create New Industry"}
              </div>
              <div className="text-xs text-slate-400 font-normal">
                {editingIndustry ? "Update industry details and terminology" : "Set up a new industry — all 22 terms auto-filled with defaults"}
              </div>
            </div>
          </div>
        }
        width={680}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-4">
          <Form.Item
            name="name"
            label="Industry Name"
            rules={[{ required: true, message: "Industry name is required" }, { min: 2, message: "Min 2 characters" }]}
          >
            <Input className="rounded-lg" placeholder="e.g. Pest Control" maxLength={100} />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea className="rounded-lg" rows={2} placeholder="Brief description of this industry sector" maxLength={500} showCount />
          </Form.Item>

          <div className="mb-4">
            <div className="text-sm font-medium text-slate-700 mb-2">
              Icon <span className="text-xs text-slate-400 font-normal ml-1">— choose an emoji</span>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-50 to-purple-100 border-2 border-violet-200 flex items-center justify-center text-4xl shadow-sm shrink-0">
                {selectedIcon}
              </div>
              <div className="flex-1">
                <Form.Item name="icon" noStyle>
                  <Input
                    className="rounded-lg text-lg"
                    placeholder="Type an emoji or pick below"
                    maxLength={8}
                    onChange={(e) => { const val = e.target.value.trim(); if (val) setSelectedIcon(val); }}
                    style={{ fontSize: 20, height: 44 }}
                  />
                </Form.Item>
                <p className="text-xs text-slate-400 mt-1.5">Type any emoji directly, or pick from the grid</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Quick pick</p>
              <div className="flex flex-wrap gap-1.5">
                {POPULAR_ICONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => pickIcon(emoji)}
                    className={`w-9 h-9 rounded-xl text-xl flex items-center justify-center transition-all border ${
                      selectedIcon === emoji ? "bg-violet-100 border-violet-400 shadow-sm scale-110" : "bg-white border-slate-200 hover:bg-violet-50 hover:border-violet-300"
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-4 mb-2">
            <Form.Item name="sort_order" label="Sort Order" rules={[{ type: "number", min: 0 }]}>
              <InputNumber min={0} className="w-full rounded-lg" placeholder="1" />
            </Form.Item>
            <div className="flex items-center gap-6 pt-7">
              <Form.Item name="is_active" valuePropName="checked" label="Active" className="mb-0">
                <Switch />
              </Form.Item>
            </div>
          </div>

          <Divider orientation="left">
            <span className="flex items-center gap-1.5 text-sm font-medium text-slate-600">
              <Languages size={14} /> Terminology (22 fields)
            </span>
          </Divider>

          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 mb-4">
            <p className="text-xs text-amber-700 leading-relaxed">
              All 22 terms are stored for every industry. Use industry-specific words (e.g. "Treatment" instead of "Job" for Pest Control).
              Click <strong>Fill Defaults</strong> to reset all to system defaults, then customise as needed.
            </p>
          </div>

          <TerminologyEditor value={terminology} onChange={setTerminology} onFillDefaults={fillDefaults} />

          <div className="flex gap-2 justify-end mt-6 pt-4 border-t border-slate-100">
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button
              htmlType="submit"
              type="primary"
              loading={saving}
              icon={<Building2 size={14} />}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              {editingIndustry ? "Update Industry" : "Create Industry"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
